import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@postdaily/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { parseAsStringLiteral, useQueryStates } from "nuqs";

import { AnalyticsEmptyState } from "@/components/analytics/analytics-empty-state";
import { AnalyticsKpiCards } from "@/components/analytics/analytics-kpi-cards";
import { AudienceGrowthCard } from "@/components/analytics/audience-growth-card";
import { ChannelPerformanceTable } from "@/components/analytics/channel-performance-table";
import { DateRangeControl } from "@/components/analytics/date-range-control";
import { EngagementOverTimeChart } from "@/components/analytics/engagement-over-time-chart";
import { ExportButton } from "@/components/analytics/export-button";
import { PostsAnalyticsTable } from "@/components/analytics/posts-analytics-table";
import { ProviderBreakdownCard } from "@/components/analytics/provider-breakdown-card";
import { TagPerformanceCard } from "@/components/analytics/tag-performance-card";
import { TopPostsCard } from "@/components/analytics/top-posts-card";
import { useAnalyticsEntitlements } from "@/components/analytics/use-analytics-entitlements";
import {
  type AnalyticsRangeState,
  useAnalyticsRange,
} from "@/components/analytics/use-analytics-range";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/analytics")({
  component: RouteComponent,
});

const analyticsTabs = ["overview", "posts", "audience"] as const;

function RouteComponent() {
  const { currentWorkspace } = useCurrentWorkspace();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Analytics"
        description="How your content and audience are performing across every channel."
      />
      {currentWorkspace ? (
        <WorkspaceAnalytics workspaceId={currentWorkspace.id} />
      ) : (
        <AnalyticsSkeleton />
      )}
    </div>
  );
}

function WorkspaceAnalytics({ workspaceId }: { workspaceId: string }) {
  const analyticsEntitlements = useAnalyticsEntitlements(workspaceId);
  const range = useAnalyticsRange(analyticsEntitlements.retentionDays);
  const [{ tab }, setTab] = useQueryStates({
    tab: parseAsStringLiteral(analyticsTabs).withDefault("overview"),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          // Controlled, so this only fires when the user picks another tab —
          // never for the initial selection.
          onValueChange={(value) => {
            if (
              value === "overview" ||
              value === "posts" ||
              value === "audience"
            ) {
              void setTab({ tab: value });
              track("analytics_tab_viewed", { tab: value });
            }
          }}
          value={tab}
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="posts">Posts</TabsTrigger>
            <TabsTrigger value="audience">Audience</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl
            range={range}
            retentionDays={analyticsEntitlements.retentionDays}
          />
          <ExportButton
            canExport={analyticsEntitlements.canExportCsv}
            from={range.from}
            to={range.to}
            workspaceId={workspaceId}
          />
        </div>
      </div>

      {tab === "overview" ? (
        <OverviewTab range={range} workspaceId={workspaceId} />
      ) : null}

      {tab === "posts" ? (
        <PostsAnalyticsTable
          from={range.from}
          to={range.to}
          workspaceId={workspaceId}
        />
      ) : null}

      {tab === "audience" ? (
        <AudienceTab
          from={range.from}
          to={range.to}
          workspaceId={workspaceId}
        />
      ) : null}
    </div>
  );
}

function OverviewTab({
  range,
  workspaceId,
}: {
  range: AnalyticsRangeState;
  workspaceId: string;
}) {
  const trpc = useTRPC();

  const overview = useQuery(
    trpc.analytics.overview.queryOptions({
      from: range.from,
      to: range.to,
      workspaceId,
    }),
  );
  const channels = useQuery(
    trpc.analytics.channelsSummary.queryOptions({
      from: range.from,
      to: range.to,
      workspaceId,
    }),
  );
  const tags = useQuery(
    trpc.analytics.tagsRollup.queryOptions({
      from: range.from,
      to: range.to,
      workspaceId,
    }),
  );
  const topPosts = useQuery(
    trpc.analytics.postsTable.queryOptions({
      from: range.from,
      limit: 5,
      sortBy: "engagement",
      sortDir: "desc",
      to: range.to,
      workspaceId,
    }),
  );

  if (overview.isPending) {
    return <AnalyticsSkeleton />;
  }

  if (overview.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Analytics unavailable</AlertTitle>
        <AlertDescription>{overview.error.message}</AlertDescription>
      </Alert>
    );
  }

  const data = overview.data;

  // Empty states: nothing published in range, or published but never synced.
  if (data.publishedTargets === 0 && data.syncedAt === null) {
    return <AnalyticsEmptyState variant="no-posts" />;
  }
  if (data.publishedTargets > 0 && data.syncedAt === null) {
    return <AnalyticsEmptyState variant="syncing" />;
  }

  return (
    <>
      <AnalyticsKpiCards kpis={data.kpis} syncedAt={data.syncedAt} />
      <EngagementOverTimeChart series={data.series} />
      <div className="grid gap-4 lg:grid-cols-2">
        <ProviderBreakdownCard rows={data.providerBreakdown} />
        {channels.isPending ? (
          <Skeleton className="h-72 w-full rounded-2xl" />
        ) : channels.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Channels unavailable</AlertTitle>
            <AlertDescription>{channels.error.message}</AlertDescription>
          </Alert>
        ) : (
          <ChannelPerformanceTable rows={channels.data} />
        )}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {topPosts.isPending ? (
          <Skeleton className="h-80 w-full rounded-2xl" />
        ) : topPosts.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Top posts unavailable</AlertTitle>
            <AlertDescription>{topPosts.error.message}</AlertDescription>
          </Alert>
        ) : (
          <TopPostsCard items={topPosts.data.items} />
        )}
        {tags.isPending ? (
          <Skeleton className="h-80 w-full rounded-2xl" />
        ) : tags.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Tags unavailable</AlertTitle>
            <AlertDescription>{tags.error.message}</AlertDescription>
          </Alert>
        ) : (
          <TagPerformanceCard rows={tags.data} />
        )}
      </div>
      {data.syncedAt ? (
        <p className="text-muted-foreground text-xs">
          Metrics as of {dayjs(data.syncedAt).format("MMM D, YYYY h:mm A")} —
          refreshed automatically every 15 minutes.
        </p>
      ) : null}
    </>
  );
}

function AudienceTab({
  from,
  to,
  workspaceId,
}: {
  from: Date;
  to: Date;
  workspaceId: string;
}) {
  const trpc = useTRPC();
  const audience = useQuery(
    trpc.analytics.audience.queryOptions({ from, to, workspaceId }),
  );

  if (audience.isPending) {
    return <Skeleton className="h-96 w-full rounded-2xl" />;
  }

  if (audience.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Audience data unavailable</AlertTitle>
        <AlertDescription>{audience.error.message}</AlertDescription>
      </Alert>
    );
  }

  return <AudienceGrowthCard audience={audience.data} />;
}

function AnalyticsSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <Skeleton className="h-80 w-full rounded-2xl" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-72 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    </div>
  );
}
